import { useState } from 'react';
import { NEEDS } from '../../lib/needs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Shield, Trash2, UserPlus } from 'lucide-react';
import { Button, Card, TextField } from '@tcrd/ui';
import { useToast } from '../../components/toast';
import { api, errorMessage } from '../../lib/api';
import type { TrustedContactDto } from '../../lib/types';
import { normalizePhone } from '../auth/PhoneGate';

/** Seguridad del perfil: PIN de abordaje (clientes) y contactos de confianza (todos). */
export function SafetySettings({ customer }: { customer: boolean }) {
  const qc = useQueryClient();
  const toast = useToast();
  const contacts = useQuery({ queryKey: ['trusted-contacts'], queryFn: () => api<TrustedContactDto[]>('/users/me/trusted-contacts') });
  const safety = useQuery({ queryKey: ['safety-settings'], queryFn: () => api<{ pinEnabled: boolean; needs: string[] }>('/customers/me/safety'), enabled: customer });
  const setNeeds = async (needs: string[]) => {
    try {
      qc.setQueryData(['safety-settings'], await api('/customers/me/safety', { method: 'PUT', body: { needs } }));
    } catch (e) {
      toast(errorMessage(e), 'danger');
    }
  };
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [busy, setBusy] = useState(false);

  const setPin = async (pinEnabled: boolean) => {
    try {
      qc.setQueryData(['safety-settings'], await api('/customers/me/safety', { method: 'PUT', body: { pinEnabled } }));
      toast(pinEnabled ? 'PIN activado: el conductor te lo pedirá al subir' : 'PIN desactivado', 'success');
    } catch (e) {
      toast(errorMessage(e), 'danger');
    }
  };

  const add = async () => {
    const phone = normalizePhone(form.phone);
    if (!phone) return toast('Escribe un teléfono válido, por ejemplo 809 555 1234', 'danger');
    setBusy(true);
    try {
      qc.setQueryData(['trusted-contacts'], await api('/users/me/trusted-contacts', { body: { name: form.name, phone, email: form.email || undefined } }));
      setForm({ name: '', phone: '', email: '' });
      setAdding(false);
    } catch (e) {
      toast(errorMessage(e), 'danger');
    } finally {
      setBusy(false);
    }
  };
  const update = async (id: string, body: Record<string, unknown>) => {
    try {
      qc.setQueryData(['trusted-contacts'], await api(`/users/me/trusted-contacts/${id}`, { method: body.delete ? 'DELETE' : 'PATCH', body: body.delete ? undefined : body }));
    } catch (e) {
      toast(errorMessage(e), 'danger');
    }
  };

  const list = contacts.data ?? [];
  return (
    <Card className="p-4 flex flex-col gap-4" id="seguridad">
      <p className="text-sm font-semibold flex items-center gap-2"><Shield className="size-5 text-[#2563eb]" aria-hidden /> Seguridad</p>

      {customer && (
        <label className="tap flex items-start gap-3">
          <KeyRound className="size-5 mt-0.5 text-[var(--text-muted)]" aria-hidden />
          <span className="flex-1">
            <span className="block text-sm font-medium">Verificar mis viajes con PIN</span>
            <span className="block text-xs text-[var(--text-muted)]">Recibirás un código de 4 dígitos; el viaje no inicia hasta que se lo digas al conductor.</span>
          </span>
          <input type="checkbox" className="size-5 mt-1 accent-[#E5252A]" checked={!!safety.data?.pinEnabled} disabled={safety.isLoading} onChange={(e) => void setPin(e.target.checked)} />
        </label>
      )}

      {customer && (
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium">Necesidades especiales</p>
          <p className="text-xs text-[var(--text-muted)]">Tu conductor las verá en cada solicitud para prepararse.</p>
          <div className="flex gap-2 flex-wrap">
            {NEEDS.map((n) => {
              const list = safety.data?.needs ?? [];
              const on = list.includes(n.id);
              return (
                <button key={n.id} type="button" aria-pressed={on} disabled={!safety.data} onClick={() => void setNeeds(on ? list.filter((x) => x !== n.id) : [...list, n.id])} className={`tap h-9 px-3 rounded-full border text-sm ${on ? 'border-brand-red bg-brand-red/10 font-semibold' : 'border-[var(--border)]'}`}>
                  {n.emoji} {n.label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium">Contactos de confianza</p>
        <p className="text-xs text-[var(--text-muted)]">Reciben tu viaje en vivo y los avisos de emergencia. Con correo, se les envía automáticamente al iniciar cada viaje.</p>
        {list.map((c) => (
          <div key={c.id} className="flex items-center gap-3 rounded-2xl border border-[var(--border)] px-3 py-2">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{c.name}</p>
              <p className="text-xs text-[var(--text-muted)] truncate">{c.phone}{c.email ? ` · ${c.email}` : ''}</p>
              {c.email && (
                <label className="mt-1 flex items-center gap-2 text-xs">
                  <input type="checkbox" className="size-4 accent-[#E5252A]" checked={c.autoShare} onChange={(e) => void update(c.id, { autoShare: e.target.checked })} />
                  Enviarle cada viaje automáticamente
                </label>
              )}
            </div>
            <button className="tap size-9 rounded-full flex items-center justify-center text-[var(--text-muted)]" onClick={() => void update(c.id, { delete: true })} aria-label={`Quitar a ${c.name}`}>
              <Trash2 className="size-4" />
            </button>
          </div>
        ))}
        {adding ? (
          <div className="flex flex-col gap-2 rounded-2xl bg-[var(--surface-2)] p-3">
            <TextField label="Nombre" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <TextField label="Teléfono (WhatsApp)" type="tel" inputMode="tel" placeholder="809 555 1234" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <TextField label="Correo (opcional)" type="email" inputMode="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <Button size="sm" variant="secondary" onClick={() => setAdding(false)}>Cancelar</Button>
              <Button size="sm" loading={busy} disabled={form.name.trim().length < 2 || !form.phone.trim()} onClick={() => void add()}>Guardar</Button>
            </div>
          </div>
        ) : (
          list.length < 5 && (
            <Button size="sm" variant="secondary" icon={<UserPlus className="size-4" />} onClick={() => setAdding(true)}>
              Agregar contacto
            </Button>
          )
        )}
      </div>
    </Card>
  );
}
